import React, { createContext, useContext, useMemo, useState, useEffect, useCallback } from "react";
import axios from "axios";

const AUTH_STORAGE_KEY = "cueTrackUser";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const ROLES = {
  ADMIN: "Admin",
  MANAGER: "Manager",
};

function loadStoredUser() {
  if (typeof window === "undefined") return null;
  try {
    const stored = window.localStorage.getItem(AUTH_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error("Failed to load auth state:", error);
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => loadStoredUser());

  useEffect(() => {
    if (user) {
      window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(user));
    } else {
      window.localStorage.removeItem(AUTH_STORAGE_KEY);
    }
  }, [user]);

  const login = useCallback(async ({ role, username, password }) => {
    // Manager signs in with the shared PIN (sent as `password`); Admin uses username+password.
    const payload = role === ROLES.MANAGER ? { role, password } : { username, password };
    const response = await axios.post(`${API}/login`, payload);
    const nextUser = {
      username,
      role: response.data.role,
      token: response.data.access_token,
    };
    setUser(nextUser);
    return nextUser;
  }, []);

  const logout = useCallback(() => {
    setUser(null);
  }, []);

  const hasRole = useCallback(
    (allowedRoles) => {
      if (!user || !Array.isArray(allowedRoles)) return false;
      return allowedRoles.includes(user.role);
    },
    [user],
  );

  const value = useMemo(
    () => ({
      user,
      login,
      logout,
      hasRole,
      isAdmin: user?.role === ROLES.ADMIN,
      isManager: user?.role === ROLES.MANAGER,
      ROLES,
    }),
    [user, login, logout, hasRole],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
